/**
 * Admin Dispute Management Page
 *
 * Dashboard for managing disputes with filtering, detail view,
 * and lifecycle actions (review, resolve, escalate, close).
 *
 * API Endpoints:
 *   GET  /api/v1/admin/disputes
 *   GET  /api/v1/admin/disputes/:id
 *   POST /api/v1/admin/disputes/:id/review
 *   POST /api/v1/admin/disputes/:id/resolve
 *   POST /api/v1/admin/disputes/:id/escalate
 *   POST /api/v1/admin/disputes/:id/close
 *
 * Story: NEW-005-FE - Dispute Management Dashboard (Admin UI)
 * MVP: 1.5.2 - Booking Payment Domain Model
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiRequest from '../../services/api';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Modal from '../../components/shared/Modal';
import {
  AlertCircle,
  CheckCircle,
  XCircle,
  RefreshCw,
  Eye,
  Filter,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Search
} from 'lucide-react';

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'opened', label: 'Open' },
  { value: 'under_review', label: 'Under Review' },
  { value: 'escalated', label: 'Escalated' },
  { value: 'resolved_customer', label: 'Resolved (Customer)' },
  { value: 'resolved_guide', label: 'Resolved (Guide)' },
  { value: 'closed', label: 'Closed' },
];

const ROLE_OPTIONS = [
  { value: '', label: 'All Roles' },
  { value: 'customer', label: 'Customer' },
  { value: 'guide', label: 'Guide' },
];

const RESOLUTION_TYPES = [
  { value: 'full_refund', label: 'Full Refund to Customer' },
  { value: 'partial_refund', label: 'Partial Refund' },
  { value: 'no_refund', label: 'No Additional Refund' },
  { value: 'rebook', label: 'Rebook at No Cost' },
  { value: 'dismissed', label: 'Dismiss Dispute' },
];

const STATUS_BADGES = {
  opened: { label: 'Open', color: 'bg-red-100 text-red-800', icon: AlertCircle },
  under_review: { label: 'Under Review', color: 'bg-yellow-100 text-yellow-800', icon: Search },
  escalated: { label: 'Escalated', color: 'bg-red-100 text-red-800 ring-1 ring-red-300', icon: AlertTriangle },
  resolved_customer: { label: 'Resolved', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  resolved_guide: { label: 'Resolved', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  closed: { label: 'Closed', color: 'bg-gray-100 text-gray-600', icon: XCircle },
};

export default function DisputeManagement() {
  const navigate = useNavigate();
  const [disputes, setDisputes] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [statusCounts, setStatusCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Detail
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Action modals
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [showEscalateModal, setShowEscalateModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [resolutionType, setResolutionType] = useState('');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [refundAdjustmentDollars, setRefundAdjustmentDollars] = useState('');
  const [favoredParty, setFavoredParty] = useState('customer');
  const [actionNotes, setActionNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState(null);

  const fetchDisputes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (statusFilter) {params.set('status', statusFilter);}
      if (roleFilter) {params.set('openedByRole', roleFilter);}
      params.set('page', currentPage.toString());
      params.set('limit', '20');

      const response = await apiRequest(`/api/v1/admin/disputes?${params.toString()}`);

      if (response.success) {
        setDisputes(response.data.disputes || []);
        setPagination(response.data.pagination || { page: 1, limit: 20, total: 0, totalPages: 0 });
        setStatusCounts(response.data.statusCounts || {});
      }
    } catch (err) {
      setError(err.message || 'Failed to load disputes');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, roleFilter, currentPage]);

  useEffect(() => {
    fetchDisputes();
  }, [fetchDisputes]);

  const fetchDetail = async (disputeId) => {
    try {
      setDetailLoading(true);
      const response = await apiRequest(`/api/v1/admin/disputes/${disputeId}`);
      if (response.success) {
        setSelectedDispute(response.data);
      }
    } catch (err) {
      setError(err.message || 'Failed to load dispute details');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleTakeUnderReview = async () => {
    if (!selectedDispute) {return;}
    try {
      setActionLoading(true);
      setActionError(null);
      await apiRequest(`/api/v1/admin/disputes/${selectedDispute.id}/review`, { method: 'POST' });
      setSelectedDispute(null);
      fetchDisputes();
    } catch (err) {
      setActionError(err.message || 'Failed to take under review');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResolve = async () => {
    if (!selectedDispute || !resolutionType || !resolutionNotes.trim()) {return;}
    try {
      setActionLoading(true);
      setActionError(null);

      const body = {
        resolutionType,
        resolutionNotes: resolutionNotes.trim(),
        favoredParty,
      };

      if (resolutionType === 'partial_refund' && refundAdjustmentDollars !== '') {
        body.refundAdjustmentCents = Math.round(parseFloat(refundAdjustmentDollars) * 100);
      }

      await apiRequest(`/api/v1/admin/disputes/${selectedDispute.id}/resolve`, {
        method: 'POST',
        body: JSON.stringify(body),
      });

      setShowResolveModal(false);
      resetActionState();
      setSelectedDispute(null);
      fetchDisputes();
    } catch (err) {
      setActionError(err.message || 'Failed to resolve dispute');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEscalate = async () => {
    if (!selectedDispute || !actionNotes.trim()) {return;}
    try {
      setActionLoading(true);
      setActionError(null);
      await apiRequest(`/api/v1/admin/disputes/${selectedDispute.id}/escalate`, {
        method: 'POST',
        body: JSON.stringify({ notes: actionNotes.trim() }),
      });
      setShowEscalateModal(false);
      resetActionState();
      setSelectedDispute(null);
      fetchDisputes();
    } catch (err) {
      setActionError(err.message || 'Failed to escalate dispute');
    } finally {
      setActionLoading(false);
    }
  };

  const handleClose = async () => {
    if (!selectedDispute || !actionNotes.trim()) {return;}
    try {
      setActionLoading(true);
      setActionError(null);
      await apiRequest(`/api/v1/admin/disputes/${selectedDispute.id}/close`, {
        method: 'POST',
        body: JSON.stringify({ notes: actionNotes.trim() }),
      });
      setShowCloseModal(false);
      resetActionState();
      setSelectedDispute(null);
      fetchDisputes();
    } catch (err) {
      setActionError(err.message || 'Failed to close dispute');
    } finally {
      setActionLoading(false);
    }
  };

  const resetActionState = () => {
    setResolutionType('');
    setResolutionNotes('');
    setRefundAdjustmentDollars('');
    setFavoredParty('customer');
    setActionNotes('');
    setActionError(null);
  };

  const getDaysOpen = (openedAt) => {
    if (!openedAt) {return 0;}
    const diff = Date.now() - new Date(openedAt).getTime();
    return Math.floor(diff / (1000 * 60 * 60 * 24));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) {return 'N/A';}
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const renderStatusBadge = (status) => {
    const config = STATUS_BADGES[status] || STATUS_BADGES.opened;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon size={12} />
        {config.label}
      </span>
    );
  };

  // Determine available actions based on status
  const getActions = (status) => {
    switch (status) {
      case 'opened':
        return ['review'];
      case 'under_review':
        return ['resolve', 'escalate', 'close'];
      case 'escalated':
        return ['resolve', 'close'];
      default:
        return [];
    }
  };

  const isTerminalState = (status) =>
    ['resolved_customer', 'resolved_guide', 'closed'].includes(status);

  return (
    <DashboardLayout
      userRole="admin"
      activeNavId="disputes"
      onNavigate={(id, path) => path && navigate(path)}
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h1 className="text-2xl font-bold text-gray-900">Dispute Management</h1>
          <button
            onClick={fetchDisputes}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-dark disabled:opacity-50 transition text-sm font-medium"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Status Count Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500 uppercase">Open</p>
            <p className="text-2xl font-bold text-red-600">{statusCounts.opened || 0}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500 uppercase">Under Review</p>
            <p className="text-2xl font-bold text-yellow-600">{statusCounts.under_review || 0}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500 uppercase">Escalated</p>
            <p className="text-2xl font-bold text-red-600">{statusCounts.escalated || 0}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500 uppercase">Resolved</p>
            <p className="text-2xl font-bold text-green-600">
              {(statusCounts.resolved_customer || 0) + (statusCounts.resolved_guide || 0)}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500 uppercase">Closed</p>
            <p className="text-2xl font-bold text-gray-600">{statusCounts.closed || 0}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex flex-wrap items-center gap-3">
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
              className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <select
              value={roleFilter}
              onChange={(e) => { setRoleFilter(e.target.value); setCurrentPage(1); }}
              className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
            >
              {ROLE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-2">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <span className="text-sm text-red-700">{error}</span>
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
              <span className="ml-3 text-gray-600">Loading disputes...</span>
            </div>
          ) : disputes.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No disputes found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-500">Dispute</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500 hidden sm:table-cell">Opened By</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500">Status</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500 hidden md:table-cell">Opened</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500 hidden md:table-cell">Days Open</th>
                    <th className="px-4 py-3 text-right font-medium text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {disputes.map((d) => {
                    const daysOpen = getDaysOpen(d.openedAt || d.opened_at);
                    return (
                      <tr key={d.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <span className="font-mono text-xs text-gray-600">
                            {(d.id || '').slice(0, 8)}
                          </span>
                          <span className="block text-xs text-gray-500 truncate max-w-[180px]">
                            {d.experienceTitle || d.experience_title || d.bookingNumber || d.booking_number || ''}
                          </span>
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell">
                          <span className="text-gray-900 capitalize">{d.openedByRole || d.opened_by_role || 'N/A'}</span>
                        </td>
                        <td className="px-4 py-3">{renderStatusBadge(d.status)}</td>
                        <td className="px-4 py-3 text-gray-600 text-xs hidden md:table-cell">
                          {formatDate(d.openedAt || d.opened_at)}
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <span className={`text-xs font-medium ${daysOpen > 7 ? 'text-red-600' : daysOpen > 3 ? 'text-yellow-600' : 'text-gray-600'}`}>
                            {isTerminalState(d.status) ? '\u2014' : `${daysOpen}d`}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => fetchDetail(d.id)}
                            className="inline-flex items-center gap-1 text-brand-primary hover:text-brand-dark text-xs font-medium"
                          >
                            <Eye className="w-3.5 h-3.5" />
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="border-t border-gray-200 px-4 py-3 flex items-center justify-between">
              <span className="text-xs text-gray-500">
                Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage <= 1}
                  className="p-1.5 rounded border border-gray-300 disabled:opacity-30"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(pagination.totalPages, currentPage + 1))}
                  disabled={currentPage >= pagination.totalPages}
                  className="p-1.5 rounded border border-gray-300 disabled:opacity-30"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Detail Modal */}
        <Modal
          isOpen={!!selectedDispute && !showResolveModal && !showEscalateModal && !showCloseModal}
          onClose={() => setSelectedDispute(null)}
          title="Dispute Details"
          footer={
            <>
              <button
                onClick={() => setSelectedDispute(null)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
              >
                Close
              </button>
              {selectedDispute && getActions(selectedDispute.status).includes('review') && (
                <button
                  onClick={handleTakeUnderReview}
                  disabled={actionLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition"
                >
                  {actionLoading ? 'Processing...' : 'Take Under Review'}
                </button>
              )}
              {selectedDispute && getActions(selectedDispute.status).includes('close') && (
                <button
                  onClick={() => { setShowCloseModal(true); resetActionState(); }}
                  className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition"
                >
                  Close Dispute
                </button>
              )}
              {selectedDispute && getActions(selectedDispute.status).includes('escalate') && (
                <button
                  onClick={() => { setShowEscalateModal(true); resetActionState(); }}
                  className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition"
                >
                  Escalate
                </button>
              )}
              {selectedDispute && getActions(selectedDispute.status).includes('resolve') && (
                <button
                  onClick={() => { setShowResolveModal(true); resetActionState(); }}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                >
                  Resolve
                </button>
              )}
            </>
          }
        >
          {detailLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
            </div>
          ) : selectedDispute ? (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <span className="text-gray-500">Status</span>
                  <div className="mt-1">{renderStatusBadge(selectedDispute.status)}</div>
                </div>
                <div>
                  <span className="text-gray-500">Opened By</span>
                  <p className="font-medium capitalize">{selectedDispute.openedByRole || selectedDispute.opened_by_role}</p>
                </div>
                <div>
                  <span className="text-gray-500">Booking</span>
                  <p className="font-mono text-xs">{(selectedDispute.bookingId || selectedDispute.booking_id || '').slice(0, 8)}</p>
                </div>
                <div>
                  <span className="text-gray-500">Experience</span>
                  <p className="font-medium">{selectedDispute.experienceTitle || selectedDispute.experience_title || 'N/A'}</p>
                </div>
              </div>

              {selectedDispute.reason && (
                <div>
                  <span className="text-gray-500">Reason</span>
                  <p className="mt-1 p-3 bg-gray-50 rounded-md">{selectedDispute.reason}</p>
                </div>
              )}

              {selectedDispute.desiredOutcome && (
                <div>
                  <span className="text-gray-500">Desired Outcome</span>
                  <p className="mt-1 p-3 bg-gray-50 rounded-md">{selectedDispute.desiredOutcome || selectedDispute.desired_outcome}</p>
                </div>
              )}

              {selectedDispute.evidenceUrls && selectedDispute.evidenceUrls.length > 0 && (
                <div>
                  <span className="text-gray-500">Evidence</span>
                  <ul className="mt-1 space-y-1">
                    {selectedDispute.evidenceUrls.map((url, i) => (
                      <li key={i}>
                        <a href={url} target="_blank" rel="noopener noreferrer" className="text-brand-primary hover:underline text-xs break-all">
                          {url}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedDispute.resolutionType && (
                <div className="pt-2 border-t border-gray-200">
                  <span className="text-gray-500">Resolution</span>
                  <p className="font-medium capitalize">{selectedDispute.resolutionType || selectedDispute.resolution_type}</p>
                  {selectedDispute.resolutionNotes && (
                    <p className="mt-1 p-3 bg-gray-50 rounded-md">{selectedDispute.resolutionNotes}</p>
                  )}
                </div>
              )}

              {actionError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700">{actionError}</p>
                </div>
              )}
            </div>
          ) : null}
        </Modal>

        {/* Resolve Modal */}
        <Modal
          isOpen={showResolveModal}
          onClose={() => { setShowResolveModal(false); resetActionState(); }}
          title="Resolve Dispute"
          footer={
            <>
              <button
                onClick={() => { setShowResolveModal(false); resetActionState(); }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleResolve}
                disabled={actionLoading || !resolutionType || !resolutionNotes.trim()}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition"
              >
                {actionLoading ? 'Resolving...' : 'Resolve Dispute'}
              </button>
            </>
          }
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Resolution Type <span className="text-red-500">*</span>
              </label>
              <select
                value={resolutionType}
                onChange={(e) => setResolutionType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="">Select resolution...</option>
                {RESOLUTION_TYPES.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </div>

            {resolutionType === 'partial_refund' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Refund Adjustment Amount ($)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={refundAdjustmentDollars}
                  onChange={(e) => setRefundAdjustmentDollars(e.target.value)}
                  placeholder="e.g. 25.00"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Favored Party <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-4">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="favoredParty"
                    value="customer"
                    checked={favoredParty === 'customer'}
                    onChange={(e) => setFavoredParty(e.target.value)}
                    className="text-brand-primary"
                  />
                  <span className="text-sm">Customer</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="favoredParty"
                    value="guide"
                    checked={favoredParty === 'guide'}
                    onChange={(e) => setFavoredParty(e.target.value)}
                    className="text-brand-primary"
                  />
                  <span className="text-sm">Guide</span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Resolution Notes <span className="text-red-500">*</span>
              </label>
              <textarea
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
                placeholder="Explain the resolution decision..."
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-none"
              />
            </div>

            {actionError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700">{actionError}</p>
              </div>
            )}
          </div>
        </Modal>

        {/* Escalate Modal */}
        <Modal
          isOpen={showEscalateModal}
          onClose={() => { setShowEscalateModal(false); resetActionState(); }}
          title="Escalate Dispute"
          footer={
            <>
              <button
                onClick={() => { setShowEscalateModal(false); resetActionState(); }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleEscalate}
                disabled={actionLoading || !actionNotes.trim()}
                className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 transition"
              >
                {actionLoading ? 'Escalating...' : 'Escalate Dispute'}
              </button>
            </>
          }
        >
          <div className="space-y-4">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                Escalating this dispute will flag it for senior review. Please provide context.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Escalation Notes <span className="text-red-500">*</span>
              </label>
              <textarea
                value={actionNotes}
                onChange={(e) => setActionNotes(e.target.value)}
                placeholder="Explain why this needs escalation..."
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-none"
              />
            </div>
            {actionError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700">{actionError}</p>
              </div>
            )}
          </div>
        </Modal>

        {/* Close Modal */}
        <Modal
          isOpen={showCloseModal}
          onClose={() => { setShowCloseModal(false); resetActionState(); }}
          title="Close Dispute"
          footer={
            <>
              <button
                onClick={() => { setShowCloseModal(false); resetActionState(); }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleClose}
                disabled={actionLoading || !actionNotes.trim()}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 transition"
              >
                {actionLoading ? 'Closing...' : 'Close Dispute'}
              </button>
            </>
          }
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Closing Notes <span className="text-red-500">*</span>
              </label>
              <textarea
                value={actionNotes}
                onChange={(e) => setActionNotes(e.target.value)}
                placeholder="Explain why this dispute is being closed..."
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-none"
              />
            </div>
            {actionError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700">{actionError}</p>
              </div>
            )}
          </div>
        </Modal>
      </div>
    </DashboardLayout>
  );
}
